import { useState, useRef, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const reviews = [
  { name: "James T.", location: "Melbourne", text: "The house looked better than when we moved in. Genuinely impressed with the attention to detail." },
  { name: "Sarah M.", location: "Sydney", text: "Reliable, professional and easy to deal with. Booking was simple and the team turned up right on time." },
  { name: "Lisa K.", location: "Brisbane", text: "Our office always looks spotless. We've been using OZ CLEAN for months and they never miss a beat." },
  { name: "Mark D.", location: "Perth", text: "Best end of lease clean we've ever had. Got our full bond back without a single issue." },
  { name: "Tom R.", location: "Gold Coast", text: "Carpets came up like new and the windows were absolutely streak-free. Highly recommend." },
  { name: "Emma W.", location: "Adelaide", text: "Booked online Sunday night, cleaned Tuesday. No drama, no delays. Easiest clean I've ever booked." },
];

const AVERAGE = 4.9;
const TOTAL = 142;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const ReviewsSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);

  const updateCardWidth = useCallback(() => {
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const firstCard = scrollRef.current.querySelector<HTMLElement>("[data-review-card]");
      if (firstCard) setCardWidth(firstCard.offsetWidth + 24);
    });
  }, []);

  useEffect(() => {
    updateCardWidth();
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateCardWidth, 150);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateCardWidth]);

  const scrollTo = (index: number) => {
    const clamped = Math.max(0, Math.min(reviews.length - 1, index));
    if (!scrollRef.current || !cardWidth) return;
    scrollRef.current.scrollTo({ left: clamped * cardWidth, behavior: "smooth" });
    setActiveIndex(clamped);
  };

  const onScroll = () => {
    if (!scrollRef.current || !cardWidth) return;
    const idx = Math.round(scrollRef.current.scrollLeft / cardWidth);
    setActiveIndex(idx);
  };

  return (
    <section id="reviews" className="bg-[#f7f7f7] py-20 md:py-28">
      <div className="container max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-[#111111] leading-tight">
            What Our Customers Say
          </h2>
          <p className="text-[#111111]/70 mt-2 text-base">
            Trusted by homeowners and businesses across Melbourne.
          </p>

          <div className="mt-7 inline-flex items-center gap-4">
            <span className="text-5xl md:text-6xl font-black text-[#111111] leading-none">
              {AVERAGE}
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FFD400] text-[#FFD400]" />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-[#111111]/70">
                  {TOTAL} reviews
                </span>
                <span className="flex items-center gap-1.5 text-xs text-[#111111]/70">
                  <GoogleIcon />
                  Based on Google Reviews
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {reviews.map((r) => (
              <div
                key={r.name}
                data-review-card
                className="snap-start shrink-0 w-[85%] sm:w-[75%] md:w-[calc(50%-12px)] bg-white rounded-2xl p-8 flex flex-col shadow-[0_2px_16px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(0,102,255,0.12)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-[18px] h-[18px] fill-[#FFD400] text-[#FFD400]" />
                  ))}
                </div>
                <p className="text-[#111111]/75 leading-relaxed flex-1 text-base">
                  "{r.text}"
                </p>
                <div className="mt-7 pt-5 border-t border-[#111111]/5 flex flex-col">
                  <span className="font-semibold text-sm text-[#111111]/75">{r.name} - {r.location}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => scrollTo(activeIndex - 1)}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#111111]/10 items-center justify-center shadow-sm hover:shadow-md transition-shadow disabled:opacity-30"
            disabled={activeIndex === 0}
            aria-label="Previous review"
          >
            <ChevronLeft className="w-5 h-5 text-[#111111]" />
          </button>
          <button
            onClick={() => scrollTo(activeIndex + 1)}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[#111111]/10 items-center justify-center shadow-sm hover:shadow-md transition-shadow disabled:opacity-30"
            disabled={activeIndex >= reviews.length - 1}
            aria-label="Next review"
          >
            <ChevronRight className="w-5 h-5 text-[#111111]" />
          </button>
        </div>

        <div className="flex justify-center gap-1 mt-6">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to review ${i + 1}`}
              className="p-2 flex items-center justify-center"
            >
              <span
                className={`block h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? "bg-[#0066FF] w-5"
                    : "bg-[#111111]/15 w-2 hover:bg-[#111111]/30"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
